// src/components/AgentSwitcher.jsx
//
// Dropdown for agency owners to switch between Agency View, their own
// Agent Workspace, and any of their agency's other agents.
//
// Renders nothing unless the current user has canSwitch === true.

import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';

const API_BASE = process.env.REACT_APP_API_URL || '';

export default function AgentSwitcher() {
  const { user, switchToAgent } = useAuth();
  const [agents, setAgents] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // Only render if the operator can switch.
  const canSwitch = !!(user && user.canSwitch);

  const loadAgents = useCallback(async () => {
    if (!canSwitch) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/workspace/switchable-agents`, {
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setAgents(data.agents || []);
      }
    } catch (e) {
      console.error('[AgentSwitcher] load failed:', e);
    } finally {
      setLoading(false);
    }
  }, [canSwitch]);

  useEffect(() => {
    if (open) loadAgents();
  }, [open, loadAgents]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function onClick(e) {
      if (!e.target.closest('[data-agent-switcher]')) setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  if (!canSwitch) return null;

  // What to show as the current label.
  const label = user?.actingAs
    ? `Viewing: ${user.actingAs.name || user.actingAs.email}`
    : 'Agency View';

  // Split agents into "self" (the operator themselves) and "others".
  const operatorId = user?.id;
  const selfEntry = agents.find((a) => a.id === operatorId) || null;
  const otherAgents = agents.filter((a) => a.id !== operatorId);

  const currentlyActingId = user?.actingAs?.id || null;

  function isActive(agentId) {
    if (!currentlyActingId && agentId === null) return true; // Agency View
    return currentlyActingId === agentId;
  }

  return (
    <div data-agent-switcher className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-md border text-sm font-medium transition-colors ${
          user?.actingAs
            ? 'bg-amber-50 border-amber-300 text-amber-900 hover:bg-amber-100'
            : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
        }`}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="truncate max-w-[200px]">{label}</span>
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-72 bg-white border border-gray-200 rounded-md shadow-lg z-50">
          <div className="py-1">
            <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Switch Workspace
            </div>

            {/* Agency View (no overlay) */}
            <button
              type="button"
              onClick={() => switchToAgent(null)}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center justify-between ${
                isActive(null) ? 'bg-blue-50 font-semibold text-blue-900' : 'text-gray-800'
              }`}
            >
              <div>
                <div>Agency View</div>
                <div className="text-xs text-gray-500">All agents combined</div>
              </div>
              {isActive(null) && <span className="text-blue-600">●</span>}
            </button>

            {/* My Workspace (operator as agent) */}
            {selfEntry && (
              <button
                type="button"
                onClick={() => switchToAgent(selfEntry.id)}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center justify-between ${
                  isActive(selfEntry.id) ? 'bg-blue-50 font-semibold text-blue-900' : 'text-gray-800'
                }`}
              >
                <div>
                  <div>My Workspace</div>
                  <div className="text-xs text-gray-500">{selfEntry.name || selfEntry.email}</div>
                </div>
                {isActive(selfEntry.id) && <span className="text-blue-600">●</span>}
              </button>
            )}

            {/* Other agents */}
            {otherAgents.length > 0 && (
              <div className="border-t border-gray-100 mt-1 pt-1">
                <div className="px-3 py-1 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Agents
                </div>
                {otherAgents.map((a) => (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => switchToAgent(a.id)}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center justify-between ${
                      isActive(a.id) ? 'bg-blue-50 font-semibold text-blue-900' : 'text-gray-800'
                    }`}
                  >
                    <div>
                      <div>{a.name || a.email}</div>
                      <div className="text-xs text-gray-500">{a.email}</div>
                    </div>
                    {isActive(a.id) && <span className="text-blue-600">●</span>}
                  </button>
                ))}
              </div>
            )}

            {loading && (
              <div className="px-3 py-2 text-xs text-gray-500">Loading agents...</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
